import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { sendWelcomeEmailIfNeeded } from "@/lib/account-emails.functions";

type AuthContextValue = {
  user: User | null;
  session: Session | null;
  loading: boolean;
};

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const welcomeTried = useRef<Set<string>>(new Set());
  const sendWelcome = useServerFn(sendWelcomeEmailIfNeeded);

  useEffect(() => {
    const maybeSendWelcome = (s: Session | null) => {
      const u = s?.user;
      if (!u || !u.email_confirmed_at) return;
      if (welcomeTried.current.has(u.id)) return;
      welcomeTried.current.add(u.id);
      // Fire and forget — server-side guards prevent duplicates.
      sendWelcome().catch((err) => {
        console.warn("welcome email trigger failed", err);
      });
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setLoading(false);
      maybeSendWelcome(s);
    });
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
      maybeSendWelcome(data.session);
    });
    return () => sub.subscription.unsubscribe();
  }, [sendWelcome]);

  return (
    <AuthContext.Provider value={{ user: session?.user ?? null, session, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
